import { Moment } from 'moment'
import { ObjectionsModel } from '../../../../../pages/invoicesPage/invoicesPage.Model'
import { EnumCriticidad } from '../../../../oData/enums/enumCriticidad'
import { EnumDominioTarea } from '../../../../oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../../oData/enums/enumEstadoTarea'
import { EnumEstadoUsuarioAtlas } from '../../../../oData/enums/enumEstadoUsuarioAtlas'
import { EnumRolAtlas } from '../../../../oData/enums/enumRolAtlas'
import { EnumTareaAccion } from '../../../../oData/enums/enumTareaAccion'
import { EnumTipoAccion } from '../../../../oData/enums/enumTipoAccion'
import { CFExecutionModel } from '../../cfExecution/model/cfExecution'
import { ContractSummaryModel } from '../../contracts/model/contracts'
import { DocumentSummaryModel } from '../../documents/model/document'
import { InvoiceSummaryModel } from '../../invoices/model/invoices'
import { LeadSummaryModel } from '../../leads/model/leads'
import { ProcedureSummaryModel } from '../../procedures'
import { SettlementModel } from '../../settlements/model/settlements'

export interface TaskModel {
  id: number
  description: string
  assignedUser: string
  authorNameAtlas?: string | null
  criticality: EnumCriticidad
  status: EnumEstadoTarea
  creationDate: Date
  closingDate?: Date
  comments: TaskCommentModel[]
  taskType: TaskTypeModel
  companyId: number
  relatedEntityId: number
  lead?: LeadSummaryModel
  invoice?: InvoiceSummaryModel
  document?: DocumentSummaryModel
  procedure?: ProcedureSummaryModel
  contract?: ContractSummaryModel
  cfExecution?: CFExecutionModel // cannot be a summaryModel because InvoiceCycleModel is needed to access invoiceCycle/description field.
  // see taskModelRelatedEntityMap below in case of adding a new property please.
}

export interface TaskTableModel extends Omit<TaskModel, 'creationDate' | 'closingDate'> {
  creationDate: string // formatted date string
  closingDate?: string // formatted date string
}
export interface TaskModelWithObjections extends TaskModel {
  task: TaskModel
  objections: ObjectionsModel[]
}
export interface TaskCommentModel {
  id?: number
  comment: string
  user: string
  date?: string
}

export interface AtlasUserModel {
  id: number
  email: string
  authorNameAtlas: string
  rol: EnumRolAtlas
  estado: EnumEstadoUsuarioAtlas
  superuser: boolean
}

export interface TaskCommentWithDateMomentModel extends Omit<TaskCommentModel, 'date'> {
  date?: Moment
}

export interface TaskTypeModel {
  id: number
  code: string
  description: string
  criticality: EnumCriticidad
  domain: EnumDominioTarea
  deadlineDays?: number
  defaultUser: string
  actionList?: TaskActionModel[]
}

export interface TaskActionModel {
  id: number
  code: EnumTareaAccion
  description: string
  tipoAccion: EnumTipoAccion
  url?: string
}
export interface TaskActionDetailModel {
  id: number
  description: string
  criticity: EnumCriticidad
}

export interface FilterTaskRequest {
  taskType?: EnumDominioTarea[]
  criticality?: EnumCriticidad[]
  taskState?: EnumEstadoTarea[]
  startDate?: Date | null
  endDate?: Date | null
  top200results?: boolean
}

// This section helps find out which property/ies must be used to handle each Entity. Array is used to handle multiple properties just in case.
// invalidEntity is used to handle if property is null or is not available yet(for example when an executionCF is not yet ran)
export const taskModelRelatedEntityPropertyMap: Record<EnumDominioTarea, string[]> = {
  [EnumDominioTarea.Factura]: ['invoice/code', 'invoice/id'],
  [EnumDominioTarea.Sujetos]: ['invalidEntity'],
  [EnumDominioTarea.Contratos]: ['invalidEntity'],
  [EnumDominioTarea.Precios]: ['invalidEntity'],
  [EnumDominioTarea.Operaciones]: ['invalidEntity'],
  [EnumDominioTarea.Documento]: ['document/description', 'document/id'],
  [EnumDominioTarea.Tramitacion]: ['procedure/code', 'procedure/id'],
  [EnumDominioTarea.LoteLiquidacion]: ['invalidEntity'],
  [EnumDominioTarea.EjecucionCF]: [
    'cfExecution/invoiceCycle/description',
    'cfExecution/invoiceCycle/id'
  ],
  [EnumDominioTarea.Lead]: ['lead/code'],
  [EnumDominioTarea.Liquidacion]: ['invalidEntity'],
  [EnumDominioTarea.PortalClientesContrato]: ['contract/code', 'contract/id'],
  [EnumDominioTarea.PortalClientesFactura]: ['invoice/code', 'invoice/id']
}
// relatedEntity helper function
/**
 * This function helps to find out which property/ies must be used to handle each Entity.
 * @param domain  EnumDominioTarea
 * @returns  string[] | undefined . An array of string, 'invalidEntity' in case the entity is not suitable for TasksPage or undefined if the domain is not found.
 */
export function getTaskModelRelatedEntityPropertyPath(
  domain: EnumDominioTarea
): string[] | undefined {
  return taskModelRelatedEntityPropertyMap[domain]
}

// getEntityForDomain helper function
/**
 * This function helps to obtain related Entity from TaskDomain.
 * @param domain  EnumDominioTarea
 * @returns  EntityModel | undefined . Entity model based on the domain or undefined if the domain is not found.
 */

export function getEntityForDomain<T extends EnumDominioTarea>(
  domain: T,
  task: TaskModel
): EntityForDomain<T> | undefined {
  if (domain === EnumDominioTarea.Factura) return task.invoice as EntityForDomain<T>
  if (domain === EnumDominioTarea.Documento) return task.document as EntityForDomain<T>
  if (domain === EnumDominioTarea.Tramitacion) return task.procedure as EntityForDomain<T>
  if (domain === EnumDominioTarea.EjecucionCF) return task.cfExecution as EntityForDomain<T>
  if (domain === EnumDominioTarea.Lead) return task.lead as EntityForDomain<T>
  if (domain === EnumDominioTarea.PortalClientesContrato) return task.contract as EntityForDomain<T>
  if (domain === EnumDominioTarea.PortalClientesFactura) return task.invoice as EntityForDomain<T>
  return undefined
}

export type EntityForDomain<T extends EnumDominioTarea> = TaskDomainEntityMap[T]

type TaskDomainEntityMap = {
  [EnumDominioTarea.Factura]: InvoiceSummaryModel
  [EnumDominioTarea.Sujetos]: never
  [EnumDominioTarea.Contratos]: never
  [EnumDominioTarea.Precios]: never
  [EnumDominioTarea.Operaciones]: never
  [EnumDominioTarea.Documento]: DocumentSummaryModel
  [EnumDominioTarea.Tramitacion]: ProcedureSummaryModel
  [EnumDominioTarea.LoteLiquidacion]: never
  [EnumDominioTarea.EjecucionCF]: CFExecutionModel
  [EnumDominioTarea.Lead]: LeadSummaryModel
  [EnumDominioTarea.Liquidacion]: SettlementModel
  [EnumDominioTarea.PortalClientesContrato]: ContractSummaryModel
  [EnumDominioTarea.PortalClientesFactura]: InvoiceSummaryModel
}

export interface TaskPatchModel extends Omit<
  TaskModel,
  'taskType' | 'creationDate' | 'closingDate' | 'companyId'
> {
  closingDate?: string
}

export interface TaskPostModel extends Omit<
  TaskModel,
  'taskType' | 'creationDate' | 'closingDate' | 'id' | 'relatedEntityId' | 'comments'
> {
  closingDate?: string
  taskType: {
    id: number
  }
  comments?: TaskCommentModel[]
}
